/**
 * Team-size buckets for the Create Team form (GLX-1922).
 *
 * These are not display-only labels. `LARGE_TEAM_TAG` is written verbatim into
 * `galaxy_props.tags`, and galaxy-server's large-team guard decides whether a
 * team needs its own realm by comparing `String(tag).toLowerCase() === '100+'`.
 * Changing the string here silently disables that guard.
 *
 * It is mirrored from galaxy-server `src/railsApi/railsApi.service.ts`
 * (`LARGE_TEAM_TAG`) rather than imported, because the two live in different
 * repos. Both sides carry a comment pointing at the other.
 *
 * Where the 100 line sits is inherited from the pricing calculator, which
 * auto-enables an isolated database at `numberOfMembers >= 100`
 * (`usePricingCalculatorForm.tsx`). Note the two are no longer one shared
 * constant: the calculator compares a number, this form offers a bucket. If the
 * threshold ever moves, both have to change.
 */

/** Marks a team as large enough to need its own realm. Also the `100+` option's value. */
export const LARGE_TEAM_TAG = '100+';

/** Existing prod tag vocabulary for the middle bucket (51 active teams carry it). */
export const MID_TEAM_TAG = '50+';

export const TEAM_SIZE_BUCKETS = {
  under50: 'Under 50',
  mid: '50-99',
  large: LARGE_TEAM_TAG,
} as const;

export type TeamSizeBucket = (typeof TEAM_SIZE_BUCKETS)[keyof typeof TEAM_SIZE_BUCKETS];

export const TEAM_SIZE_OPTIONS: TeamSizeBucket[] = [
  TEAM_SIZE_BUCKETS.under50,
  TEAM_SIZE_BUCKETS.mid,
  TEAM_SIZE_BUCKETS.large,
];

export const TEAM_SIZE_LABEL = 'Expected team size';

export const TEAM_SIZE_HELPER_TEXT =
  'Members expected to be on-boarded over the next 12 months - include planned growth, not just day-one seats.';

/** Every size tag this form can apply, so a bucket change can replace rather than accumulate. */
export const ALL_SIZE_TAGS: string[] = [LARGE_TEAM_TAG, MID_TEAM_TAG];

/**
 * The tag a bucket implies. `Under 50` implies none - there is no prod tag for it
 * and inventing one would pollute the shared tag vocabulary.
 *
 * The growth override forces the large tag regardless of bucket: a team starting
 * at 50 that adds 60 next month should be provisioned correctly on day one.
 */
export const sizeTagForSelection = ({
  teamSize,
  growthOverride,
}: {
  teamSize: TeamSizeBucket | '';
  growthOverride: boolean;
}): string | null => {
  if (teamSize === TEAM_SIZE_BUCKETS.large || growthOverride) {
    return LARGE_TEAM_TAG;
  }
  if (teamSize === TEAM_SIZE_BUCKETS.mid) {
    return MID_TEAM_TAG;
  }
  return null;
};
